import React from 'react';
import styled from 'styled-components';
import { Content, Stack } from '@kinesis/bungle';
import { GpcSummaryTable as GpcSummaryTableData } from '@/types';
import { Title } from '@/components/title';
import { renderValueOptional, renderUnit } from '@/data/units';

// NOTE: styles stolen from bungle nursery table, but adapted to
// a very custom grid layout that works only for this table

const alignXMap = new Map([
  ['left', 'start'],
  ['center', 'center'],
  ['right', 'end'],
]);
const paddingMap = new Map([['none', 0]]);

const TableRow = styled<any>('div')(
  ({ theme }: any) => ({
    display: 'grid',
    gridColumn: '1 / -1',
    gridTemplateColumns: 'subgrid',
    '&:hover': {
      backgroundColor: theme.color.gray3,
    },
  }),
  ({ $gridRow }: any) => ({
    gridRow: $gridRow,
  }),
);

const TableCell = styled<any>('div')(
  () => ({
    alignItems: 'center',
    display: 'grid',
    '&[role="columnheader"]': {
      backgroundColor: '#fafafa',
      fontWeight: 'bold',
    },
  }),
  ({ $heading }: any) =>
    $heading
      ? {
          fontWeight: 'bold',
        }
      : undefined,
  ({ $alignX }: any) =>
    alignXMap.has($alignX)
      ? {
          justifyContent: alignXMap.get($alignX),
        }
      : {
          justifyContent: 'stretch',
        },
  ({ $padding }: any) =>
    paddingMap.has($padding)
      ? {
          padding: paddingMap.get($padding),
        }
      : {
          padding: '0 12px',
        },
  ({ theme, $showGridLines }: any) =>
    $showGridLines
      ? {
          '&:not(:first-child)': {
            borderLeft: `1px solid ${theme.borderColor}`,
          },
          [`${TableRow}:not(:first-child) &`]: {
            borderTop: `1px solid ${theme.borderColor}`,
          },
        }
      : undefined,
  ({ $gridRow }: any) => ({
    gridRow: $gridRow,
  }),
  ({ $gridColumn }: any) => ({
    gridColumn: $gridColumn,
  }),
);

const TableWrapper = styled<any>('div')(
  ({ theme }: any) => ({
    alignItems: 'stretch',
    display: 'grid',
    fontSize: theme.fontSize.medium,
    gap: 1,
    gridAutoRows: 32,
    whiteSpace: 'nowrap',
    width: '100%',
  }),
  ({ $gridTemplateColumns }: any) => ({
    gridTemplateColumns: $gridTemplateColumns,
  }),
);

const GpcSummaryTable = (props: GpcSummaryTableProps) => {
  const { data } = props;
  return (
    <Stack space='medium'>
      <Title>GPC Summary Table</Title>
      <Content
        background='white'
        padding='medium'
        border
        paddingMode='equal'
        borderRadiusSize='small'
      >
        <Content border>
          <TableWrapper
            role='table'
            $gridTemplateColumns='auto auto auto auto auto auto auto'
          >
            <TableRow role='row' $gridRow='span 2'>
              <TableCell
                role='columnheader'
                $showGridLines
                $gridRow='1 / span 2'
                $gridColumn='1 / span 2'
              >
                Sector
              </TableCell>
              <TableCell
                role='columnheader'
                $showGridLines
                $gridRow='1'
                $gridColumn='3 / span 3'
              >
                Total by scope ({renderUnit(data.unit)})
              </TableCell>
              <TableCell
                role='columnheader'
                $showGridLines
                $gridRow='1'
                $gridColumn='6 / span 2'
              >
                Total by reporting level ({renderUnit(data.unit)})
              </TableCell>
              <TableCell
                role='columnheader'
                $showGridLines
                $gridRow='2'
                $gridColumn='3'
              >
                Scope 1
              </TableCell>
              <TableCell
                role='columnheader'
                $showGridLines
                $gridRow='2'
                $gridColumn='4'
              >
                Scope 2
              </TableCell>
              <TableCell
                role='columnheader'
                $showGridLines
                $gridRow='2'
                $gridColumn='5'
              >
                Scope 3
              </TableCell>
              <TableCell
                role='columnheader'
                $showGridLines
                $gridRow='2'
                $gridColumn='6'
              >
                BASIC
              </TableCell>
              <TableCell
                role='columnheader'
                $showGridLines
                $gridRow='2'
                $gridColumn='7'
              >
                BASIC+
              </TableCell>
            </TableRow>
            {data.sectors.map((s) =>
              s.classes.length > 0 ? (
                <TableRow key={s.code} $gridRow={`span ${s.classes.length}`}>
                  <TableCell
                    role='columnheader'
                    $showGridLines
                    $gridRow={`auto / span ${s.classes.length}`}
                    $gridColumn='1'
                  >
                    {s.display}
                  </TableCell>
                  {s.classes.map((c) => (
                    <React.Fragment key={c.code}>
                      <TableCell $showGridLines>{c.display}</TableCell>
                      <TableCell $showGridLines>
                        {renderValueOptional(c.summary.scope1, data.unit)}
                      </TableCell>
                      <TableCell $showGridLines>
                        {renderValueOptional(c.summary.scope2, data.unit)}
                      </TableCell>
                      <TableCell $showGridLines>
                        {renderValueOptional(c.summary.scope3, data.unit)}
                      </TableCell>
                      <TableCell $showGridLines>
                        {renderValueOptional(c.summary.basic, data.unit)}
                      </TableCell>
                      <TableCell $showGridLines>
                        {renderValueOptional(c.summary.plus, data.unit)}
                      </TableCell>
                    </React.Fragment>
                  ))}
                </TableRow>
              ) : null,
            )}

            <TableRow>
              <TableCell
                role='columnheader'
                $showGridLines
                $gridColumn='1 / span 2'
              >
                Total
              </TableCell>
              <TableCell $showGridLines>
                {renderValueOptional(data.total.scope1, data.unit)}
              </TableCell>
              <TableCell $showGridLines>
                {renderValueOptional(data.total.scope2, data.unit)}
              </TableCell>
              <TableCell $showGridLines>
                {renderValueOptional(data.total.scope3, data.unit)}
              </TableCell>
              <TableCell $showGridLines>
                {renderValueOptional(data.total.basic, data.unit)}
              </TableCell>
              <TableCell $showGridLines>
                {renderValueOptional(data.total.plus, data.unit)}
              </TableCell>
            </TableRow>
          </TableWrapper>
        </Content>
      </Content>
    </Stack>
  );
};

export type GpcSummaryTableProps = {
  data: GpcSummaryTableData;
};

export { GpcSummaryTable };
